/**
 * Pure-fn helpers for the `site_themes.target_id` in-place launcher target
 * (#117 — parity with TSAI's `widget/theme.js` + `uiController.js`).
 *
 * Contract:
 *  - Customers store an optional DOM element id in `site_themes.target_id`
 *    via the Showroom dashboard. When set, the widget loader mounts the
 *    `<skinny-widget>` custom element INSIDE that DOM element (replacing
 *    the floating launcher entirely) so the chat panel renders inline in
 *    the host page's normal flow rather than as a fixed corner FAB.
 *  - The dashboard surface persists either `myMountId` or `#myMountId`
 *    (both common operator habits); `normalizeTargetId` strips the leading
 *    `#` and trims whitespace before validation so the stored value is
 *    always a bare HTML id.
 *  - HTML5 ids may not contain whitespace and must be at least one
 *    character; `target_id` IS allowed to contain colons, periods, hyphens,
 *    etc., so we validate against the conservative "no whitespace, no
 *    empties" subset and reject anything else as `null` (loader falls back
 *    to floating launcher with a `console.warn`).
 *  - Pure functions only — no DOM access, no I/O — so the rule engine is
 *    fully unit-testable. The loader provides the live `document` object.
 */

/**
 * Maximum length for a stored `target_id`. HTML5 has no hard cap but
 * 100 characters covers every realistic CSS-in-JS / framework-generated
 * id we've seen in TSAI customer pages, and aligns with the Zod max we
 * apply server-side. Anything longer is rejected as `null`.
 */
export const TARGET_ID_MAX_LENGTH = 100;

/**
 * Strip a leading `#`, trim whitespace, and validate the result against
 * the conservative HTML5 id subset (no whitespace, at least one char,
 * at most `TARGET_ID_MAX_LENGTH` chars).
 *
 * Returns the cleaned id, or `null` when the input is missing / blank /
 * invalid. Pure function — no side effects.
 */
export function normalizeTargetId(input: unknown): string | null {
  if (typeof input !== 'string') {
    return null;
  }
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const stripped = trimmed.startsWith('#') ? trimmed.slice(1).trim() : trimmed;
  if (stripped.length === 0 || stripped.length > TARGET_ID_MAX_LENGTH) {
    return null;
  }
  if (/\s/.test(stripped)) {
    return null;
  }
  return stripped;
}

/**
 * Decision returned by `resolveMountTarget`.
 *
 *  - `floating`  — no `target_id` configured; loader appends to `document.body`
 *                  and `SkinnyWidgetApp` renders the floating FAB + panel.
 *  - `inline`    — `target_id` resolved to a real DOM element; loader appends
 *                  there and `SkinnyWidgetApp` switches to in-flow layout.
 *  - `missing`   — `target_id` configured but no matching element on the
 *                  host page; loader falls back to floating + logs a warn.
 */
export type MountTargetDecision =
  | { readonly kind: 'floating' }
  | { readonly kind: 'inline'; readonly element: HTMLElement; readonly targetId: string }
  | { readonly kind: 'missing'; readonly targetId: string };

/**
 * Minimal `document`-shaped interface so this module can stay pure-fn
 * and unit-testable without jsdom. The loader passes the real
 * `document` at runtime.
 */
export interface MountTargetDocument {
  readonly getElementById: (id: string) => HTMLElement | null;
}

/**
 * Decide where to mount the `<skinny-widget>` host element.
 *
 *  - If `rawTargetId` normalizes to `null` → floating launcher (default).
 *  - If `rawTargetId` resolves to an element → inline mount inside it.
 *  - If `rawTargetId` is set but no element exists → `missing` (caller
 *    is responsible for the `console.warn` + falling back to `document.body`).
 *
 * Pure function — caller supplies the `document` shape.
 */
export function resolveMountTarget(
  rawTargetId: unknown,
  doc: MountTargetDocument | null | undefined,
): MountTargetDecision {
  const normalized = normalizeTargetId(rawTargetId);
  if (normalized === null) {
    return { kind: 'floating' };
  }
  if (!doc) {
    return { kind: 'missing', targetId: normalized };
  }
  const element = doc.getElementById(normalized);
  if (element) {
    return { kind: 'inline', element, targetId: normalized };
  }
  return { kind: 'missing', targetId: normalized };
}
